import type { Problem } from "@baton/api";
import { useI18n } from "@baton/i18n";
import { Link, RequestId } from "@baton/ui";
import { useRef, type ReactNode } from "react";
import { useRouteMeta } from "../routes/meta.ts";

/**
 * FR-015-090, `contracts/screens.md` § Error and utility screens, `spec.md` § Edge cases:
 * the no-access panel rendered in place for a 403 or a 404 on a container, with the
 * reason, the workspace switcher and a request-access link. The workspace owner's address
 * is not in the workspace read, so the link is a `mailto:` with the workspace named in the
 * subject and no address, which the mail client asks for. A screen whose refusal is narrower
 * than the workspace (a project settings page for a member who is not its admin) names it
 * through `title` and `body`.
 */
export interface NoAccessProps {
  readonly workspace: string;
  readonly problem: Problem | null;
  readonly switcher: ReactNode;
  readonly title?: string;
  readonly body?: string;
}

export function NoAccess({ workspace, problem, switcher, title, body }: NoAccessProps) {
  const { t } = useI18n();
  const heading = useRef<HTMLHeadingElement>(null);
  useRouteMeta({ screen: title === undefined ? "noAccess.title" : { text: title }, focus: heading });
  const subject = encodeURIComponent(t("noAccess.requestSubject", { workspace }));
  return (
    <div className="b-no-access" role="alert">
      <h1 ref={heading} tabIndex={-1} className="b-no-access__title">
        {title ?? t("noAccess.title")}
      </h1>
      <p className="b-no-access__body">{body ?? problem?.detail ?? t("noAccess.body", { workspace })}</p>
      {problem !== null && problem.request_id.length > 0 && (
        <p className="b-no-access__body">
          <RequestId id={problem.request_id} />
        </p>
      )}
      <div className="b-no-access__switcher" data-chrome>
        {switcher}
      </div>
      <div className="b-no-access__actions">
        <Link href={`mailto:?subject=${subject}`} variant="standalone" external>
          {t("noAccess.requestAccess")}
        </Link>
      </div>
    </div>
  );
}
